import { Button, Message } from '@alifd/next';
import { Box } from '@mui/material';
import { Cascader, Form, Input, Modal, Select, Table, TableProps } from 'antd';
import axios from 'axios';
import moment from 'moment';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';

import PageHeader from '@/components/PageHeader';
import DownloadButton from '@/components/UI/DownloadButton';
import { BackStageContext } from '@/contexts/BackStageContext';
import client from '@/lib/feathers';
import useService from '@/lib/hooks/useService';
import getMqttClient from '@/lib/mqtt';
import { RootState } from '@/redux/store';
import { ChargingPile } from '@/types/database/chargingPile/chargingPile';
import { MqttPileData } from '@/types/database/chargingPile/chargingPileMqttData';
import {
  generateHouseHoldNumberCascader,
  Option,
} from '@/utils/generateHouseHoldNumberCascader';

import { pileStatus } from '../tools';
import UploadExcel from './components/UploadExcel';
import { TableContainer } from './styles';
import { ChargingPileListTableDataType } from './types';
import { getName, getSorter } from './utils';

const { useForm, Item } = Form;

const ChargingPileManagement: React.FC = () => {
  const [showEdit, setShowEdit] = useState(false);
  const [editingPile, setEditingPile] =
    useState<ChargingPileListTableDataType>();
  const [pilesStatus, setPilesStatus] = useState<{
    [id: string]: MqttPileData['status'];
  }>({});
  const [communityEnglishName, setCommunityEnglishName] = useState('');
  const [dataSource, setDataSource] = useState<ChargingPileListTableDataType[]>(
    []
  );
  const [filteredData, setFilteredData] = useState<
    ChargingPileListTableDataType[]
  >([]);
  const [filter, setFilter] = useState<{
    house: string[];
    parking: string | undefined;
    status: MqttPileData['status'] | undefined;
  }>({
    house: [],
    parking: undefined,
    status: undefined,
  });
  const [filterHouseOptions, setFilterHouseOptions] = useState<Option[]>([]);
  const [filterParkingOptions, setFilterParkingOptions] = useState<Option[]>(
    []
  );
  const [editHouseOptions, setEditHouseOptions] = useState<Option[]>([]);
  // TODO: 獨立車位列表後補回
  // const [editParkingOptions, setEditParkingOptions] = useState<
  //   { label: string; value: string }[]
  // >([]);
  // const [formValues, setFormValues] = useState<{
  //   householdNumber: string[] | undefined;
  //   parkingNumber: string | undefined;
  //   chargingPileID: string | undefined;
  // }>();

  const user = useSelector((state: RootState) => state.user);

  const { backStageUpdateTrigger } = useContext(BackStageContext);

  const [form] = useForm();

  const [houses] = useService({
    collectionName: 'residentAccountNumbers',
    query: {
      $select: ['householdNumber', 'parkingSpace'],
    },
  });

  const [piles, updatePiles] = useService<ChargingPile>({
    collectionName: 'chargingPile',
  });

  useEffect(() => {
    return updatePiles();
  }, [backStageUpdateTrigger, updatePiles]);

  useEffect(() => {
    (async () => {
      setCommunityEnglishName(await getName());
    })();
  }, []);

  useEffect(() => {
    if (piles.length > 0) {
      setDataSource(
        piles.map((pile) => ({
          ...pile,
          status: pilesStatus[pile._id] || 'noSignal',
          key: pile._id,
        }))
      );
      const newHouseNumbers: string[] = [];
      piles.forEach((pile) => {
        if (typeof pile.householdNumber === 'string') {
          newHouseNumbers.push(pile.householdNumber);
        }
      });
      setFilterHouseOptions(
        generateHouseHoldNumberCascader(
          newHouseNumbers.sort((a, b) => a.localeCompare(b))
        )
      );
    }
  }, [piles, pilesStatus]);

  useEffect(() => {
    if (piles.length > 0) {
      const newParkingNumbers: string[] = [];
      piles
        .filter((pile) =>
          filter.house.length > 0
            ? pile.householdNumber === filter.house.join('-')
            : true
        )
        .forEach((pile) => {
          if (typeof pile.parkingNumber === 'string') {
            newParkingNumbers.push(pile.parkingNumber);
          }
        });
      setFilterParkingOptions(
        generateHouseHoldNumberCascader(newParkingNumbers)
      );
    }
  }, [filter, piles]);

  useEffect(() => {
    if (houses.length > 0) {
      setEditHouseOptions(
        generateHouseHoldNumberCascader(
          houses.map((house) => house.householdNumber)
        )
      );
    }
  }, [houses]);

  // TODO: 獨立車位列表後補回
  // useEffect(() => {
  //   if (houses.length > 0) {
  //     const selectedHouseNumber: string[] = formValues?.householdNumber || [];
  //     setEditParkingOptions(() => {
  //       return (
  //         houses
  //           .find(
  //             (house) =>
  //               house.householdNumber === selectedHouseNumber?.join('-')
  //           )
  //           ?.parkingSpace.map((parking: string) => ({
  //             label: parking,
  //             value: parking,
  //           })) || []
  //       );
  //     });
  //   }
  // }, [formValues, houses]);

  // 取得充電樁狀態
  useEffect(() => {
    if (communityEnglishName) {
      const mqttClient = getMqttClient();
      const path = `EZchargingPile/status/${communityEnglishName}/chargingPiles/+/status`;
      if (piles.length > 0) {
        try {
          mqttClient.subscribe(path);
          mqttClient.on('message', (topic, message) => {
            const path = topic.split('/');
            const id = path.at(-2);
            setPilesStatus((prevStatus) => {
              const keys = ['standby', 'charging', 'noSignal', 'waiting'];
              if (
                id &&
                prevStatus[id]?.toString() !== message.toString() &&
                keys.includes(message.toString())
              ) {
                return {
                  ...prevStatus,
                  [id]: message.toString() as MqttPileData['status'],
                };
              } else {
                return prevStatus;
              }
            });
          });
        } catch (e) {
          console.error(e);
        }
      }
      return () => {
        mqttClient.end();
      };
    }
    return;
  }, [communityEnglishName, piles]);

  useEffect(() => {
    setDataSource(
      piles.map((pile) => ({
        ...pile,
        key: pile._id,
        status: pilesStatus[pile.chargingPileID] || 'noSignal',
      }))
    );
  }, [piles, pilesStatus]);

  useEffect(() => {
    const newData = dataSource.filter((pile) => {
      if (
        filter.house &&
        filter.house.length > 0 &&
        pile.householdNumber !== filter.house.join('-')
      ) {
        return false;
      }
      if (filter.parking && pile.parkingNumber !== filter.parking) {
        return false;
      }
      if (typeof filter.status === 'string' && pile.status !== filter.status) {
        return false;
      }
      return true;
    });
    setFilteredData(newData);
  }, [filter, dataSource]);

  const columns: TableProps<ChargingPileListTableDataType>['columns'] = useMemo(
    () => [
      {
        title: '編號',
        dataIndex: 'chargingPileID',
        sorter: getSorter('chargingPileID'),
      },
      {
        title: '戶號',
        dataIndex: 'householdNumber',
        sorter: getSorter('householdNumber'),
      },
      {
        title: '車位編號',
        dataIndex: 'parkingNumber',
        sorter: getSorter('parkingNumber'),
      },
      {
        title: '狀態',
        dataIndex: 'status',
        render: (status: string) => {
          return pileStatus[status]?.text;
        },
        sorter: getSorter('status'),
      },
      {
        title: '操作',
        render: (_, record) => {
          return (
            <Button
              onClick={() => {
                setShowEdit(true);
                setEditingPile(record);
                form.setFieldsValue({
                  ...record,
                  householdNumber:
                    record?.householdNumber?.split('-') || undefined,
                });
              }}
            >
              編輯
            </Button>
          );
        },
      },
    ],
    [form]
  );

  const handleUpdatePileData = useCallback(
    async (
      values: ChargingPile & {
        householdNumber: string[];
      }
    ) => {
      if (editingPile) {
        try {
          await client.service('chargingPile').patch(editingPile._id, {
            ...values,
            householdNumber: values?.householdNumber?.join('-') || null,
          });
          setShowEdit(false);
          setEditingPile(undefined);
          form.resetFields();
          updatePiles();
          Message.success('更新成功');

          await axios.patch(
            `${location.origin}/ezcon/api/bill/updateChargingPileBasicFee`,
            {},
            {
              headers: {
                authorization: localStorage.getItem('feathers-jwt'),
              },
            }
          );
        } catch (error) {
          console.error(error);
          Message.error('更新失敗');
        }
      }
    },
    [editingPile, form, updatePiles]
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
      }}
    >
      <PageHeader
        title="充電樁列表"
        breadcrumbs={[{ name: '汽車充電樁' }, { name: '充電樁列表' }]}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: '10px',
        }}
      >
        <Cascader
          value={filter.house}
          onChange={(value) =>
            setFilter({
              ...filter,
              house: value ? value.map((v) => v.toString()) : [],
              parking: undefined,
            })
          }
          options={filterHouseOptions}
          placeholder="請選擇戶號"
          allowClear
          style={{ width: 200 }}
        />
        <Cascader
          value={filter.parking?.split('-')}
          onChange={(value) =>
            setFilter((prev) => ({ ...prev, parking: value.join('-') }))
          }
          options={filterParkingOptions}
          placeholder="請選擇車位編號"
          allowClear
          style={{ width: 200 }}
        />
        <Select
          value={filter.status}
          onChange={(value) =>
            setFilter((prev) => ({ ...prev, status: value }))
          }
          placeholder="請選擇充電樁狀態"
          allowClear
          style={{ width: 200 }}
          options={[
            { label: '充電中', value: 'charging' },
            { label: '待機中', value: 'standby' },
            { label: '無訊號', value: 'noSignal' },
            { label: '排隊中', value: 'waiting' },
          ]}
        />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: '10px',
          }}
        >
          <DownloadButton
            url="/ezcon/api/chargingPileListPageExcelData"
            responseFileType="blob"
            fileName={`${moment().format('YY-MM-DD')}充電樁列表.xlsx`}
          >
            下載充電樁列表Excel
          </DownloadButton>
          {!user?.userGroup?.allow?.some(
            (func: { name: string }) => func.name === '社區管理'
          ) && <UploadExcel />}
        </Box>
      </Box>
      <TableContainer>
        <Table
          bordered
          dataSource={filteredData}
          columns={columns}
          showSorterTooltip={false}
          pagination={{
            defaultPageSize: 10,
            // hideOnSinglePage: true,
          }}
          sortDirections={['descend', 'ascend', 'descend']}
        />
      </TableContainer>
      <Modal
        title="編輯充電樁"
        visible={showEdit}
        onCancel={() => setShowEdit(false)}
        onOk={() => form.submit()}
        forceRender
        okText="確定"
        cancelText="取消"
      >
        <Form
          form={form}
          onFinish={handleUpdatePileData}
          // TODO: 獨立車位列表後補回
          // onValuesChange={(changeValues, values) => {
          //   if (Object.keys(changeValues).includes('householdNumber')) {
          //     form.setFieldsValue({
          //       parkingNumber: undefined,
          //     });
          //   }
          //   setFormValues(values);
          // }}
          labelCol={{ span: 6 }}
          wrapperCol={{ span: 18 }}
        >
          <Item label="充電樁編號" name="chargingPileID">
            <Input disabled />
          </Item>
          <Item label="戶號" name="householdNumber">
            <Cascader options={editHouseOptions} placeholder="請選擇戶號" />
          </Item>
          <Item label="車位編號" name="parkingNumber">
            {/* TODO: 獨立車位列表後補回 */}
            {/* <Select options={editParkingOptions} placeholder="請先選擇戶號" /> */}
            <Input />
          </Item>
        </Form>
      </Modal>
    </Box>
  );
};

export default ChargingPileManagement;
